import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";

import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { offeringHasNoAccreditationStep } from "@equidefi/shared";

import { useLockHistory } from "@/hooks/useLockHistory";
import { useOfferingAnalytics } from "@/hooks/useOfferingAnalytics";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import useWorkflowPosition from "../../../hooks/useWorkflowPosition";
import { DocuSealWorkflowStep } from "./DocuSealWorkflowStep";

export const WorkflowAgreements = () => {
  const navigate = useNavigate();

  const { investment, offering, refetchInvestment } = useWorkflowContext();
  const analytics = useOfferingAnalytics(offering, investment);
  useWorkflowPosition(7);

  useEffect(() => {
    refetchInvestment();
  }, []);

  useLockHistory();

  useDocumentTitle(["Subscription Agreement", investment?.offering?.name, investment?.issuer?.name]);

  /** @description Triggers the move to the next (and possibly final?) step */
  const handleContinue = useCallback(async () => {
    // If offering is a Reg A OR an expedited investment OR Reg D 506(b), workflow is complete, go to vault
    if (offeringHasNoAccreditationStep(offering) || investment.expedited) {
      analytics.track("complete");
      navigate("/vault/dashboard", {
        state: {
          completed: true,
          isPaid: !!investment?.payments,
          investment_id: investment.id,
        },
      });
      return;
    }

    navigate(`/offerings/${offering.slug}/${investment.id}/accreditation`);
  }, [analytics, navigate, offering, investment]);

  const hasAdditionalSigners = useMemo(
    () => !!investment?.signing_parties?.find((party) => party.order === 2 || party.role === "signer2"),
    [investment],
  );

  return (
    <DocuSealWorkflowStep
      hasAdditionalSigners={hasAdditionalSigners}
      onContinue={handleContinue}
      agreement={investment?.investment_agreements?.at(0)}
    />
  );
};
